import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { useOrderDetail } from "./api";
import { ExtensionRequestSheet } from "./ExtensionRequestSheet";
import { formatDate, formatDateTime, formatMoneyCents } from "./format";
import { IssueReporterDialog } from "./IssueReporterDialog";
import { ExpiryReminderBanner } from "./ExpiryReminderBanner";
import { ProposedWindowBanner } from "./ProposedWindowBanner";
import type { OrderDetailPayload } from "./types";

// Phase 4b extension-requests FR-1 — CTA visible only on active rentals
// (driver delivered, customer not yet picked up).
const EXTENDABLE_STATUSES = new Set(["delivered", "on_rent"]);

// Phase 4a customer-portal FR-6 — order detail with header, timeline,
// line items, agreement download, invoices, COI status, and the
// embedded Issue Reporter.
export function OrderDetailPage(): JSX.Element {
  const { order_number = "" } = useParams<{ order_number: string }>();
  const location = useLocation();
  const [reporterOpen, setReporterOpen] = useState(location.hash === "#issue");
  const [extensionOpen, setExtensionOpen] = useState(false);
  const q = useOrderDetail(order_number);

  if (q.isLoading) return <div className="p-4 text-sm text-zinc-600">Loading…</div>;
  if (q.isError || !q.data) return <div className="p-4 text-sm text-red-700">Couldn&rsquo;t load this order.</div>;

  return (
    <DetailBody
      payload={q.data}
      orderNumber={order_number}
      reporterOpen={reporterOpen}
      onOpenReporter={() => setReporterOpen(true)}
      onCloseReporter={() => setReporterOpen(false)}
      extensionOpen={extensionOpen}
      onOpenExtension={() => setExtensionOpen(true)}
      onCloseExtension={() => setExtensionOpen(false)}
    />
  );
}

interface BodyProps {
  payload: OrderDetailPayload;
  orderNumber: string;
  reporterOpen: boolean;
  onOpenReporter: () => void;
  onCloseReporter: () => void;
  extensionOpen: boolean;
  onOpenExtension: () => void;
  onCloseExtension: () => void;
}

// eslint-disable-next-line max-lines-per-function
function DetailBody({
  payload, orderNumber, reporterOpen, onOpenReporter, onCloseReporter,
  extensionOpen, onOpenExtension, onCloseExtension
}: BodyProps): JSX.Element {
  const o = payload.order;
  const addr = o.site_address;

  return (
    <div className="space-y-4 text-sm">
      <Link to="/portal" className="text-xs underline">
        ← Dashboard
      </Link>

      <header className="space-y-1 border-b border-zinc-200 pb-2">
        <h1 className="text-lg font-semibold">Order #{o.order_number}</h1>
        <p className="text-zinc-600">
          Status: <strong>{o.status}</strong>
          {o.pickup_notice_at ? ` · Pickup requested ${formatDateTime(o.pickup_notice_at)}` : ""}
        </p>
        <p className="text-xs text-zinc-500">
          {[addr.line1, addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}
        </p>
      </header>

      {/* Phase 4c availability-procurement FR-5. Surfaces an admin
          proposed-change banner when the customer needs to accept or
          reject a different rental window. Falsy when no proposal. */}
      {payload.proposed_window ? (
        <ProposedWindowBanner orderNumber={orderNumber} proposal={payload.proposed_window} />
      ) : null}

      {o.status === "pending_procurement" && !payload.proposed_window ? (
        <ProcurementSourcingNotice />
      ) : null}

      {/* Phase 4d rental-expiry-reminders FR-7. Banner with the
          extend + schedule-pickup CTAs when the rental is inside the
          tier-relevant reminder window. The server's
          `active_expiry_reminder` is null outside the window or when
          the customer opted out. */}
      {payload.active_expiry_reminder ? (
        <ExpiryReminderBanner
          orderNumber={orderNumber}
          reminder={payload.active_expiry_reminder}
        />
      ) : null}

      <Section title="Timeline">
        {payload.timeline.length === 0 ? (
          <Hint>No timeline events yet.</Hint>
        ) : (
          <ol className="space-y-1">
            {payload.timeline.map((row, idx) => (
              <li key={`${row.action}-${idx}`} className="text-xs text-zinc-700">
                {formatDateTime(row.at)} — <strong>{row.action}</strong>
              </li>
            ))}
          </ol>
        )}
      </Section>

      <Section title="Line items">
        {payload.lines.length === 0 ? (
          <Hint>No line items.</Hint>
        ) : (
          <ul className="space-y-1">
            {payload.lines.map((l) => (
              <li key={l.id}>
                {l.type_name} ({formatDate(l.rental_start_at)} → {formatDate(l.rental_end_at)})
                {l.addons.length > 0 ? (
                  <ul className="ml-4 list-disc text-xs text-zinc-600">
                    {l.addons.map((a, i) => (
                      <li key={i}>
                        {a.name} — {formatMoneyCents(a.amount_cents)}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Agreement">
        {payload.agreement.signed && payload.agreement.pdf_url ? (
          <a
            href={payload.agreement.pdf_url}
            className="underline"
            target="_blank"
            rel="noreferrer"
            data-testid="agreement-pdf-link"
          >
            Download agreement PDF (v{payload.agreement.template_version})
          </a>
        ) : (
          <Hint>Agreement not yet signed.</Hint>
        )}
      </Section>

      <Section title="Invoices">
        {payload.invoices.length === 0 ? (
          <Hint>No invoices yet.</Hint>
        ) : (
          <ul className="space-y-1">
            {payload.invoices.map((inv) => (
              <li key={inv.id} className="flex items-center justify-between">
                <span>INV-{inv.id} · {formatMoneyCents(inv.total_cents)}</span>
                <span className="text-xs text-zinc-500">{inv.status ?? "—"}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="COI on file">
        {payload.coi.approved_at ? (
          <p className="text-xs text-zinc-700">Approved {formatDate(payload.coi.approved_at)}.</p>
        ) : payload.coi.required ? (
          <p className="text-xs text-red-700">COI required — upload from the Profile tab.</p>
        ) : (
          <Hint>Not required for this order.</Hint>
        )}
      </Section>

      <Section title="Support tickets">
        {payload.tickets.length === 0 ? (
          <Hint>No tickets reported.</Hint>
        ) : (
          <ul className="space-y-2">
            {payload.tickets.map((t) => (
              <li key={t.id} className="rounded border border-zinc-200 p-2">
                <div className="text-sm font-medium">{t.subject}</div>
                <div className="text-xs text-zinc-500">
                  {t.severity} · {t.status} · {formatDateTime(t.created_at)}
                </div>
                {t.admin_reply ? (
                  <div className="mt-1 text-xs text-zinc-700">JDR: {t.admin_reply}</div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <div className="pt-2 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onOpenReporter}
          className="min-h-[44px] rounded border border-zinc-300 px-4 py-2 text-sm"
          data-testid="open-issue-reporter"
        >
          Report an issue
        </button>
        {EXTENDABLE_STATUSES.has(o.status) && o.pickup_date && (
          <button
            type="button"
            onClick={onOpenExtension}
            className="min-h-[44px] rounded border border-zinc-300 px-4 py-2 text-sm"
            data-testid="open-extension-request"
          >
            Request extension
          </button>
        )}
      </div>

      {reporterOpen ? (
        <IssueReporterDialog
          orderNumber={orderNumber}
          onClose={onCloseReporter}
          onSubmitted={onCloseReporter}
        />
      ) : null}

      {extensionOpen && o.pickup_date ? (
        <ExtensionRequestSheet
          orderNumber={orderNumber}
          currentPickupDate={o.pickup_date}
          cardLast4={null}
          onClose={onCloseExtension}
        />
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="space-y-1 rounded border border-zinc-200 p-3">
      <h2 className="text-base font-semibold">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Hint({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className="text-xs text-zinc-500">{children}</p>;
}

// Phase 4c availability-procurement FR-12 (intent_created) — the
// generic "we're sourcing" banner that shows on any
// `pending_procurement` order that is NOT awaiting a customer
// response to a proposed change. The customer already got an email;
// this is the in-portal reminder.
function ProcurementSourcingNotice(): JSX.Element {
  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-3"
         data-testid="procurement-sourcing-notice">
      <h2 className="text-sm font-semibold text-amber-900">JDR is sourcing your equipment</h2>
      <p className="mt-1 text-sm text-amber-900">
        We don&rsquo;t currently have one of these in stock, but{" "}
        <strong>we&rsquo;re working on getting one</strong>. We&rsquo;ll confirm within 24 hours
        and email you. The hold on your card stays in place; we won&rsquo;t capture funds
        until your rental is confirmed.
      </p>
    </div>
  );
}
